import { AnimationEvent, useEffect, useState } from 'react';

import SolidProgressBar from '@r1-frontend/ui-react/components/SolidProgressBar';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { useTimer } from '@r1-frontend/shared/hooks/useTimer';

import * as ST from './styled';

export interface InteractiveLoaderProps {
    textItems: string[],
    delay: number,
    loopSegmentAtIndex?: number,
    videoSrc: string,
    finished?: boolean,
    className?: string,
}

const InteractiveLoader = ({
    textItems = [],
    delay = 5,
    loopSegmentAtIndex = 0,
    videoSrc,
    finished = false,
    className,
}: InteractiveLoaderProps) => {
    const { seconds, start, timeStatus } = useTimer();

    const [progress, setProgress] = useState(0);
    const [textStatusCounter, setTextStatusCounter] = useState(0);
    const [items, setItems] = useState(textItems);

    const totalSeconds = textItems.length * delay;
    const percent = 1 - Math.round(seconds * 100 / totalSeconds) * 0.01;

    const animationStartHandler = () => {
        if (loopSegmentAtIndex && textStatusCounter ===  textItems.length - 1) {
            setItems(textItems.slice(loopSegmentAtIndex));
        }
    };

    const animationEndHandler = (event: AnimationEvent<HTMLDivElement>) => {
        if (loopSegmentAtIndex && textStatusCounter < textItems.length) {
            setTextStatusCounter(prev => prev + 1);
        }
        // @ts-ignore
        event.target?.parentNode.appendChild(event.target);
    };

    useEffect(() => {
        switch (timeStatus) {
            case 'waiting':
                setProgress(0);
                break;
            case 'progress':
                setProgress(percent);
                break;
            default:
                setProgress(1);
                break;
        }
    }, [percent, timeStatus]);

    useEffect(() => {
        start(totalSeconds);
    }, []);

    return (
        <ST.Wrapper className={className}>
            <ST.Video
                src={videoSrc}
                autoPlay
                muted
                preload='auto'
                loop
                playsInline
                width={205}
                height={205}
                data-test='loader-video'
            />

            <ST.ProgressBarWrapper>
                <SolidProgressBar
                    background={COLORS.Outline_Light}
                    color={COLORS.Brand_Blue}
                    width={4}
                    borderRadius={10}
                    progress={!finished ? progress : 1}
                    data-test='loader-progress'
                />
            </ST.ProgressBarWrapper>

            <ST.StatusContainer>
                <Paragraph4>
                    {items.map((item, idx) => (
                        <ST.StatusItem
                            key={idx}
                            onAnimationEnd={animationEndHandler}
                            onAnimationStart={animationStartHandler}
                            delay={delay}
                            data-test='loader-text-status'
                        >{item}</ST.StatusItem>
                    ))
                }
                </Paragraph4>
            </ST.StatusContainer>
        </ST.Wrapper>
    );
};

export default InteractiveLoader;
